import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import React from 'react';
import ImageWithPlaceholder from '../../../shared/components/ImageWithPlaceholderProps';
import { Provider } from '../../../provider/types';

// Define default colors to use if not provided
const defaultColors = {
    cardBackground: 'rgba(0, 0, 0, 0.95)',
  cardBorder: '#DDB88919',
  cardBorderSelected: "#22c55e49",
  cardShadow: 'rgba(221,184,137,0.07)',
  cardShadowSelected: "rgba(34,197,94,0.25)",
  titleText: '#DDB889',
  subtitleText: '#9CA3AF',
  iconColor: '#DDB889B2',
  selectionBackground: "#22c55e",
  selectionIcon: "#FFFFFF"
};

interface ProviderGridProps {
  providers: { node: Provider }[];
  isClickable?: boolean;
  onProviderClick?: (provider: Provider) => void;
  selectedProviders?: Provider | Provider[] | null;
  colors?: {
    cardBackground?: string;
    cardBorder?: string;
    cardBorderSelected?: string;
    placeholderGradientFrom?: string;
    placeholderIconBackground?: string;
    titleText?: string;
    subtitleText?: string;
    iconColor?: string;
    selectionBackground?: string;
    selectionIcon?: string;
  };
  itemsPerRow?: number;
}

const ProviderGrid: React.FC<ProviderGridProps> = ({
  providers,
  isClickable = false,
  onProviderClick,
  selectedProviders = null,
  colors = {},
  itemsPerRow = 4
}) => {
  // Merge provided colors with defaults
  const themeColors = { ...defaultColors, ...colors };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.2
      }
    }
  };

  const isProviderSelected = (provider: Provider) => {
    if (!selectedProviders) return false;
    if (Array.isArray(selectedProviders)) {
      return selectedProviders.some(selected => selected.id === provider.id);
    }
    return selectedProviders.id === provider.id;
  };

  const handleProviderClick = (provider: Provider) => {
    if (!isClickable || !onProviderClick) return;
    onProviderClick(provider);
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`;
  };

  // Determine grid layout class based on itemsPerRow
  const getGridLayoutClass = () => {
    switch (itemsPerRow) {
      case 2:
        return 'lp-grid-cols-2';
      case 3:
        return 'lp-grid-cols-2 sm:lp-grid-cols-3';
      case 4:
        return 'lp-grid-cols-2 sm:lp-grid-cols-3 md:lp-grid-cols-4';
      case 5:
        return 'lp-grid-cols-2 sm:lp-grid-cols-3 md:lp-grid-cols-4 lg:lp-grid-cols-5';
      case 6:
        return 'lp-grid-cols-2 sm:lp-grid-cols-3 md:lp-grid-cols-4 lg:lp-grid-cols-6';
      default:
        return 'lp-grid-cols-2 sm:lp-grid-cols-3 md:lp-grid-cols-4';
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className={`lp-grid lp-gap-3 lp-w-full ${getGridLayoutClass()}`}
    >
      {providers.map(({ node: provider }) => {
        const isSelected = isProviderSelected(provider);

        return (
          <motion.div
  key={provider.id}
  variants={itemVariants}
  className={`
    lp-rounded-xl lp-overflow-hidden
    ${isClickable ? 'lp-cursor-pointer' : ''}
  `}
  style={{
    backgroundColor: themeColors.cardBackground,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: isSelected ? themeColors.cardBorderSelected : themeColors.cardBorder,
    boxShadow: isSelected
      ? `0 0 15px ${themeColors.cardShadowSelected}`
      : `0 0 10px ${themeColors.cardShadow}`
  }}
          >
            <div
              className="lp-relative lp-p-3 lp-rounded-lg lp-transition-colors lp-duration-200"
              style={{
                backgroundColor: isSelected
                  ? themeColors.placeholderGradientFrom || `${themeColors.titleText}19`
                  : themeColors.cardBackground,
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: isSelected ? themeColors.cardBorderSelected : themeColors.cardBorder
              }}
            >
              <div className="lp-flex lp-items-center lp-gap-3">
                {/* Provider avatar/initials */}
                {provider.image ? (
                  <div className="lp-w-10 lp-h-10 lp-rounded-full lp-overflow-hidden lp-shrink-0">
                    <ImageWithPlaceholder
                      image={provider.image}
                      alt={`${provider.firstName} ${provider.lastName}`}
                      className="lp-w-full lp-h-full lp-object-cover"
                    />
                  </div>
                ) : (
                  <div
                    className="lp-w-10 lp-h-10 lp-rounded-full lp-flex lp-items-center lp-justify-center lp-shrink-0"
                    style={{
                      backgroundColor: isSelected
                        ? themeColors.selectionBackground
                        : themeColors.placeholderIconBackground || `${themeColors.titleText}33`
                    }}
                  >
                    {isSelected ? (
                      <CheckCircle
                        className="lp-w-5 lp-h-5"
                        style={{ color: themeColors.selectionIcon }}
                      />
                    ) : (
                      <span
                        className="lp-font-medium lp-text-sm"
                        style={{ color: themeColors.titleText }}
                      >
                        {getInitials(provider.firstName, provider.lastName)}
                      </span>
                    )}
                  </div>
                )}

                {/* Provider name */}
                <div className="lp-min-w-0 lp-pr-2">
                  <h4
                    className="lp-font-medium lp-text-sm lp-truncate"
                    style={{ color: themeColors.titleText }}
                  >
                    {provider.firstName}
                  </h4>
                  <p
                    className="lp-text-xs lp-truncate"
                    style={{ color: themeColors.iconColor || `${themeColors.titleText}B3` }}
                  >
                    {provider.lastName}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        );
      })}
    </motion.div>
  );
};

export default ProviderGrid;
